import { Component, effect, inject, input, InputSignal } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { DatePipe, NgStyle } from '@angular/common';
import { CorporateDesignService } from '../../../services/corporate-design/corporate-design.service';
import { WebTextStore } from '../../../services/api/text/web-text-store.service';
import { FilterTextsPipe } from '../../../pipes/filterTexts/filterTexts.pipe';
import { LanguagePipe } from '../../../pipes/language/language.pipe';
import {
  CONFIGURATION_SETTING_KEYS,
  EEntityType,
  ERelationType,
  IJourneyTrip,
  IOperatorMapping,
  ITariffRelationVia,
} from '@web/dtos';
import { ButtonComponent } from '../../feedback/button/button.component';
import { ConfigurationStore } from '../../../services/api/configuration/configuration-store.service';
import { ReconRouteStore } from '../../../services/api/journey-planning/recon/recon-route-store.service';
import { LoadingAnimationComponent } from '../../feedback/loading-animation/loading-animation.component';
import { DateFormatSupport } from '../../../services/support/date-format-support/date-format-support.service';
import { ActiveDirectTicketStore } from '../../../services/api/direct-ticket/active/active-direct-ticket-store.service';
import { OperatorMappingStore } from '../../../services/api/operator-mapping/operator-mapping-store.service';
import { OrderDirectTicketSupportService } from '../../../services/support/order-ticket-support/order-direct-ticket-support.service';

const JOURNEY_PLANNING_REDIRECT_KEY = 'IS_JOURNEY_PLANNING_REDIRECT';

@Component({
  selector: 'aw-journey-planner',
  imports: [
    FormsModule,
    ReactiveFormsModule,
    NgStyle,
    FilterTextsPipe,
    LanguagePipe,
    ButtonComponent,
    LoadingAnimationComponent,
    DatePipe,
  ],
  templateUrl: './journey-planner.component.html',
})
export class JourneyPlannerComponent {
  public readonly ctx: InputSignal<string | undefined> = input();

  private readonly configurationStore: ConfigurationStore =
    inject(ConfigurationStore);
  protected readonly reconRouteStore: ReconRouteStore = inject(ReconRouteStore);
  protected readonly corporateDesign: CorporateDesignService = inject(
    CorporateDesignService
  );
  protected readonly directTicketStore: ActiveDirectTicketStore = inject(
    ActiveDirectTicketStore
  );
  protected readonly dateFormatSupport: DateFormatSupport =
    inject(DateFormatSupport);
  protected readonly textStore: WebTextStore = inject(WebTextStore);
  private readonly operatorMappingStore: OperatorMappingStore =
    inject(OperatorMappingStore);
  private readonly orderTicketSupportService: OrderDirectTicketSupportService =
    inject(OrderDirectTicketSupportService);

  protected readonly ERelationType: typeof ERelationType = ERelationType;
  protected readonly EEntityType: typeof EEntityType = EEntityType;

  constructor() {
    effect(() => {
      const ctxString: string | undefined = this.ctx();
      if (
        ctxString &&
        !this.operatorMappingStore.isLoading() &&
        localStorage.getItem(JOURNEY_PLANNING_REDIRECT_KEY)
      ) {
        this.reconRouteStore.loadReconRoute(ctxString);
        //@TODO comment in after testing phase
        //localStorage.removeItem(JOURNEY_PLANNING_REDIRECT_KEY);
      }
    });
    effect(() => {
      const ctxString: string | undefined = this.ctx();
      const trip: IJourneyTrip = this.reconRouteStore.reconRoute();
      const via: ITariffRelationVia | undefined = this.reconRouteStore.via();
      const isReconLoading: boolean = this.reconRouteStore.isLoading();
      const isMappingsLoading: boolean = this.operatorMappingStore.isLoading();

      if (ctxString && trip && !isReconLoading && !isMappingsLoading) {
        const matchedMapping: IOperatorMapping | undefined =
          this.findMatchingOperatorMapping(trip);

        if (matchedMapping?.name) {
          this.orderTicketSupportService.tryToOrderDirectTicket(
            matchedMapping.name,
            true
          );
        } else {
          this.searchForTickets(
            trip.origin.locationReferenceId,
            trip.destination.locationReferenceId,
            trip.origin.date,
            trip.tariffSegments !== undefined
              ? trip.tariffSegments[0]?.relationExtRef
              : via?.via
          );
        }
      }
    });
  }

  private findMatchingOperatorMapping(
    trip: IJourneyTrip
  ): IOperatorMapping | undefined {
    const mappings: IOperatorMapping[] =
      this.operatorMappingStore.operatorMappings();
    for (const leg of trip.legs) {
      const match: IOperatorMapping | undefined = mappings.find(
        (mapping: IOperatorMapping) => mapping.operatorId === leg.operator.id
      );
      if (match) {
        return match;
      }
    }
    return undefined;
  }

  public redirectToJourneyPlanning(): void {
    const url: string = this.configurationStore.getStringConfiguration(
      CONFIGURATION_SETTING_KEYS.JourneyPlannerUrl
    );
    if (url) {
      localStorage.setItem(JOURNEY_PLANNING_REDIRECT_KEY, 'true');
      window.location.href = url;
    }
  }

  public formatJourneyTime(journeyTime: string): string {
    const split: string[] = journeyTime.split(':');
    return split[0] + ':' + split[1];
  }

  protected searchForTickets(
    origin: string,
    destination: string,
    date: string,
    via?: string
  ): void {
    this.directTicketStore.loadRelationTicket({
      origin: origin,
      destination: destination,
      dateTime: date,
      via,
    });
  }
}
